import React, { useState } from 'react';
import { Form, Button, ListGroup, Badge, Spinner, Alert, Row, Col } from 'react-bootstrap';

const ActiveTrades = ({ 
  activeTrades, 
  loading, 
  onAddTrade, 
  onRemoveTrade, 
  compact = false,
  hideList = false
}) => {
  const [symbol, setSymbol] = useState('');
  const [notes, setNotes] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(null);
  
  // Format symbol for display (uppercase)
  const formatSymbol = (symbol) => {
    return symbol ? symbol.toUpperCase() : '';
  };
  
  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Simple validation
    if (!symbol.trim()) {
      setError('Please enter a valid symbol');
      return;
    }
    
    // Reset error
    setError(null);
    setIsSubmitting(true);
    
    try {
      // Format symbol - convert to lowercase for storage
      const formattedSymbol = symbol.trim().toLowerCase();
      
      // Call the add API
      await onAddTrade(formattedSymbol, notes);
      
      // Reset form
      setSymbol('');
      setNotes('');
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to add symbol');
    } finally {
      setIsSubmitting(false);
    }
  };
  
  // Handle remove active trade
  const handleRemoveTrade = async (symbol) => {
    try {
      await onRemoveTrade(symbol);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to remove symbol');
    }
  };
  
  return (
    <div className="active-trades-component">
      {!compact && (
        <>
          <h2>Active Trades</h2>
          <p className="text-muted">
            Add symbols to track and receive Telegram notifications for new signals.
          </p>
        </>
      )}
      
      {error && (
        <Alert variant="danger" onClose={() => setError(null)} dismissible>
          {error}
        </Alert>
      )}
      
      <Form onSubmit={handleSubmit}>
        <Row>
          <Col md={compact ? 5 : 12} className="mb-3">
            <Form.Group>
              <Form.Label>Symbol</Form.Label>
              <Form.Control
                type="text"
                placeholder="Enter symbol (e.g., AAPL, BTC/USD)"
                value={symbol}
                onChange={(e) => setSymbol(e.target.value)}
                disabled={isSubmitting}
              />
            </Form.Group>
          </Col>
          
          <Col md={compact ? 5 : 12} className="mb-3">
            <Form.Group>
              <Form.Label>Notes (optional)</Form.Label>
              <Form.Control
                as="textarea"
                rows={compact ? 1 : 2}
                placeholder="Add notes about this trade"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                disabled={isSubmitting}
              />
            </Form.Group>
          </Col>
          
          <Col md={compact ? 2 : 12} className={compact ? "d-flex align-items-end mb-3" : ""}>
            <Button 
              variant="primary" 
              type="submit" 
              disabled={isSubmitting || !symbol.trim()}
              className={compact ? "w-100" : ""}
            >
              {isSubmitting ? (
                <>
                  <Spinner
                    as="span"
                    animation="border"
                    size="sm"
                    role="status"
                    aria-hidden="true"
                    className="me-2"
                  />
                  Adding...
                </>
              ) : (
                <>
                  <i className="bi bi-plus-circle me-1"></i> Add Symbol
                </>
              )}
            </Button>
          </Col>
        </Row>
      </Form>
      
      {/* Only show the list if hideList is false */}
      {!hideList && (
        <div className="active-trades-list">
          <h3>Current Watchlist</h3>
          
          {loading ? (
            <div className="loading-spinner">
              <Spinner animation="border" role="status">
                <span className="visually-hidden">Loading...</span>
              </Spinner>
            </div>
          ) : !activeTrades || activeTrades.length === 0 ? (
            <div className="no-data-message">
              <p>No active trades. Add symbols above to start tracking.</p>
            </div>
          ) : (
            <ListGroup>
              {activeTrades.map((trade) => (
                <ListGroup.Item key={trade.symbol} className="active-trade-item">
                  <div>
                    <strong>{formatSymbol(trade.symbol)}</strong>
                    {trade.notes && (
                      <small className="text-muted d-block">{trade.notes}</small>
                    )}
                  </div>
                  <div>
                    <Badge bg="secondary" className="me-2">
                      Added: {new Date(trade.addedAt).toLocaleDateString()}
                    </Badge>
                    <Button
                      variant="outline-danger"
                      size="sm"
                      onClick={() => handleRemoveTrade(trade.symbol)}
                    >
                      Remove
                    </Button>
                  </div>
                </ListGroup.Item>
              ))}
            </ListGroup>
          )}
        </div>
      )}
    </div>
  );
};

export default ActiveTrades;